import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import EcfBadge, { type BadgeVariant } from './EcfBadge';
import { DOC_TYPE_LABEL } from '../catalogos';
import {
  ecfService,
  type CertCaseResult,
  type CertPreview,
  type EcfConfig,
} from '../../../../services/ecf.service';

/**
 * Panel del Paso 4 de la certificación: la simulación con los datos de la empresa.
 *
 * Se carga el MISMO set del paso 2, pero con dos diferencias que la DGII exige: los comprobantes
 * salen con el RNC y la razón social reales de la empresa, y los e-NCF los genera el sistema
 * desde un número libre en lugar de usar los que trae el set.
 *
 * Ese número importa: la DGII no admite reutilizar una secuencia entre intentos, ni siquiera si
 * el comprobante fue rechazado. Por eso el campo se rellena con el siguiente bloque libre según
 * lo que ya se envió, y se puede ajustar a mano.
 */

const STATUS_BADGE: Record<string, { label: string; variant: BadgeVariant }> = {
  pendiente: { label: 'Pendiente', variant: 'muted' },
  listo: { label: 'Firmado', variant: 'info' },
  enproceso: { label: 'En proceso', variant: 'warning' },
  aceptado: { label: 'Aceptado', variant: 'success' },
  rechazado: { label: 'Rechazado', variant: 'danger' },
  error: { label: 'Error', variant: 'danger' },
};

/** Etiqueta de cada fila del recuento; '32rfce' es como el portal cuenta los consumo < 250 mil. */
function etiquetaTipo(clave: string): string {
  if (clave === '32rfce') return '32 · Consumo < 250 mil (RFCE)';
  return `${clave} · ${DOC_TYPE_LABEL[clave] ?? 'Otro'}`;
}

interface SimulacionPanelProps {
  config: EcfConfig;
}

export default function SimulacionPanel({ config }: SimulacionPanelProps) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [desde, setDesde] = useState<number>(100);

  const [preview, setPreview] = useState<CertPreview | null>(null);
  const [results, setResults] = useState<CertCaseResult[]>([]);

  const [loading, setLoading] = useState(true);
  const [previewing, setPreviewing] = useState(false);
  const [sending, setSending] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const load = useCallback(async () => {
    try {
      const { cases, desdeSugerido } = await ecfService.simResults();
      setResults(cases);
      setDesde(desdeSugerido);
    } catch (err) {
      setMessage({
        type: 'error',
        text: err instanceof Error ? err.message : 'No se pudo cargar el estado',
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const ambienteOk = config.environment === 'cert';
  const listoParaEnviar = ambienteOk && !!config.emisor_rnc && !!config.emisor_name;

  const handlePreview = async () => {
    if (!file) return;
    setPreviewing(true);
    setMessage(null);
    try {
      const p = await ecfService.simPreview(file, desde);
      setPreview(p);
      const conError = p.cases.filter((c) => c.status === 'error').length;
      setMessage(
        conError
          ? {
              type: 'error',
              text: `${conError} de ${p.total} comprobantes no se pudieron armar. Revisa el detalle antes de enviar.`,
            }
          : {
              type: 'success',
              text: `${p.total} comprobantes armados con tus datos y numerados desde el ${desde}. Nada se ha enviado todavía.`,
            }
      );
    } catch (err) {
      setMessage({
        type: 'error',
        text: err instanceof Error ? err.message : 'No se pudo procesar la simulación',
      });
    } finally {
      setPreviewing(false);
    }
  };

  const handleRun = async () => {
    if (!file || !preview) return;

    const porEnviar = preview.total - preview.yaEnviados;
    const aviso =
      `Se enviarán ${porEnviar} comprobantes a la DGII (ambiente ${preview.environment}) ` +
      `con el RNC ${preview.emisorRnc} y numerados desde el ${desde}.\n\n` +
      `Esto NO se puede deshacer: cada e-NCF queda consumido y la DGII no admite reutilizar la ` +
      `secuencia en otro intento, ni siquiera si rechaza el comprobante.\n\n¿Continuar?`;
    if (!confirm(aviso)) return;

    setSending(true);
    setMessage(null);
    try {
      const r = await ecfService.simRun(file, desde);
      await load();
      const partes = [`${r.enviados} enviados`];
      if (r.saltados) partes.push(`${r.saltados} ya estaban enviados`);
      if (r.fallidos) partes.push(`${r.fallidos} con error`);
      setMessage({ type: r.fallidos ? 'error' : 'success', text: partes.join(' · ') });
      toast[r.fallidos ? 'warning' : 'success'](`Simulación terminada: ${partes.join(', ')}`);
    } catch (err) {
      const text = err instanceof Error ? err.message : 'Falló el envío';
      setMessage({ type: 'error', text });
      toast.error(text);
    } finally {
      setSending(false);
    }
  };

  /**
   * Reintento de los rechazados. No reenvía el mismo e-NCF —la DGII no lo admite— sino que vuelve
   * a armar esos mismos casos del set con números nuevos. El plan lo calcula el backend y se
   * enseña antes de ejecutar, porque también quema secuencias.
   */
  const handleRetry = async () => {
    if (!file) {
      toast.error('Carga el mismo set que usaste para enviar.');
      return;
    }
    setRetrying(true);
    setMessage(null);
    try {
      const { plan } = await ecfService.simRetry(file, desde);
      const aviso =
        `Se reenviarán ${plan.encfs.length} comprobantes con e-NCF nuevos, desde el ${plan.desde}:\n\n` +
        `${plan.encfs.join(', ')}\n\n` +
        `Los anteriores (${plan.anteriores.map((a) => a.encf).join(', ')}) quedan quemados y se ` +
        `conservan en la lista como historial.\n\n¿Continuar?`;
      if (!confirm(aviso)) return;

      const { run } = await ecfService.simRetry(file, plan.desde, true);
      await load();
      if (run) {
        const partes = [`${run.enviados} enviados`];
        if (run.fallidos) partes.push(`${run.fallidos} con error`);
        setMessage({ type: run.fallidos ? 'error' : 'success', text: partes.join(' · ') });
        toast[run.fallidos ? 'warning' : 'success'](`Reintento: ${partes.join(', ')}`);
      }
    } catch (err) {
      const text = err instanceof Error ? err.message : 'No se pudo reintentar';
      setMessage({ type: 'error', text });
      toast.error(text);
    } finally {
      setRetrying(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      setResults(await ecfService.simRefreshStatus());
      toast.success('Estados actualizados');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'No se pudieron actualizar los estados');
    } finally {
      setRefreshing(false);
    }
  };

  const handleReset = async () => {
    if (
      !confirm(
        'Esto borra el registro local de la simulación, pero NO lo deshace en la DGII.\n\n' +
          'El número inicial sugerido volverá al valor por defecto, así que ajústalo a mano para ' +
          'no repetir secuencias ya usadas.\n\n¿Continuar?'
      )
    )
      return;
    try {
      await ecfService.simReset();
      setPreview(null);
      await load();
      toast.success('Registro local reiniciado');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'No se pudo reiniciar');
    }
  };

  const descargar = async (encf: string) => {
    try {
      await ecfService.simDownloadXml(encf);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'No se pudo descargar el XML');
    }
  };

  const filas: CertCaseResult[] = results.length ? results : preview?.cases ?? [];
  const aceptados = results.filter((r) => r.status === 'aceptado');
  const rfceAceptados = aceptados.filter((r) => r.send_via === 'rfce').length;
  const ecfAceptados = aceptados.filter((r) => r.send_via === 'ecf').length;
  // Rechazado y error se reintentan igual: en ambos casos el e-NCF ya no sirve.
  const fallidos = results.filter((r) => r.status === 'rechazado' || r.status === 'error').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-10">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {!listoParaEnviar && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-4 py-3 text-sm space-y-1">
          <p className="font-medium">
            <i className="ri-error-warning-line mr-1"></i>
            Falta configuración antes de poder enviar
          </p>
          <ul className="list-disc list-inside space-y-0.5 text-xs">
            {!ambienteOk && (
              <li>
                El ambiente activo es <strong>{config.environment}</strong>. La simulación solo
                cuenta en CerteCF.
              </li>
            )}
            {!config.emisor_rnc && <li>Falta el RNC del emisor.</li>}
            {!config.emisor_name && (
              <li>
                Falta la razón social. Aquí sí se envía dentro del comprobante, y debe coincidir
                exactamente con la registrada para el RNC.
              </li>
            )}
          </ul>
        </div>
      )}

      {message && (
        <div
          className={`p-4 rounded-lg border text-sm ${
            message.type === 'success'
              ? 'bg-green-50 text-green-700 border-green-200'
              : 'bg-red-50 text-red-700 border-red-200'
          }`}
        >
          <i
            className={`${
              message.type === 'success' ? 'ri-checkbox-circle-line' : 'ri-error-warning-line'
            } mr-2`}
          ></i>
          {message.text}
        </div>
      )}

      {/* 1. Set y numeración */}
      <div className="border border-gray-200 rounded-lg p-4 space-y-3">
        <p className="text-sm font-medium text-gray-900">1. Carga el set y elige la numeración</p>
        <p className="text-xs text-gray-500">
          Es el mismo archivo del Paso 2. Lo que cambia es que los comprobantes se emiten con tus
          datos —{' '}
          <strong>
            {config.emisor_name ?? 'razón social sin configurar'} · RNC{' '}
            {config.emisor_rnc ?? '—'}
          </strong>{' '}
          — y con e-NCF nuevos generados por el sistema.
        </p>
        <input
          ref={fileRef}
          type="file"
          accept=".xlsx,.xls"
          onChange={(e) => {
            setFile(e.target.files?.[0] ?? null);
            setPreview(null);
          }}
          className="text-sm text-gray-700 file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border file:border-gray-300 file:bg-gray-50 file:text-sm file:text-gray-700 file:cursor-pointer hover:file:bg-gray-100"
        />

        <div className="flex items-end gap-3 flex-wrap pt-1">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Numerar desde el
            </label>
            <input
              type="number"
              min={1}
              value={desde}
              onChange={(e) => {
                setDesde(Math.max(1, Number(e.target.value) || 1));
                setPreview(null);
              }}
              className="w-40 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <p className="text-xs text-gray-500 flex-1 min-w-[16rem] pb-2">
            Cada tipo arranca en este número: el primer 31 sería{' '}
            <span className="font-mono">E31{String(desde).padStart(10, '0')}</span>. La DGII no
            admite reutilizar una secuencia entre intentos, así que si repites la simulación tiene
            que ser por encima de lo ya enviado.
          </p>
        </div>
      </div>

      {/* 2. Firmar sin enviar */}
      <div className="border border-gray-200 rounded-lg p-4 space-y-3">
        <p className="text-sm font-medium text-gray-900">2. Comprueba antes de enviar</p>
        <p className="text-xs text-gray-500">
          Arma y firma los comprobantes con tus datos, sin enviarlos.
        </p>
        <button
          onClick={handlePreview}
          disabled={!file || previewing}
          className="px-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 text-sm font-medium hover:bg-gray-50 transition-colors whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <i
            className={`${previewing ? 'ri-loader-4-line animate-spin' : 'ri-quill-pen-line'} mr-2`}
          ></i>
          {previewing ? 'Firmando...' : 'Firmar sin enviar'}
        </button>

        {preview && (
          <div className="text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded-lg p-3 space-y-2">
            <p>
              Emisor: <strong>{preview.razonSocial}</strong> · RNC {preview.emisorRnc} · ambiente{' '}
              {preview.environment}
            </p>
            <p>
              {preview.total} comprobantes numerados desde el {preview.desde ?? desde}
              {preview.yaEnviados > 0 && ` · ${preview.yaEnviados} ya enviados (se saltarán)`}
            </p>

            {/* El portal lista el conteo esperado por tipo; esto es para cuadrarlo de un vistazo. */}
            <div>
              <p className="font-medium text-gray-700 mb-1">
                Comprobantes por tipo (compáralo con el portal):
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-0.5">
                {Object.entries(preview.porTipo)
                  .sort(([a], [b]) => a.localeCompare(b))
                  .map(([clave, n]) => (
                    <div key={clave} className="flex justify-between gap-2">
                      <span>{etiquetaTipo(clave)}</span>
                      <strong>{n}</strong>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* 3. Enviar */}
      <div className="border border-gray-200 rounded-lg p-4 space-y-3">
        <p className="text-sm font-medium text-gray-900">3. Envía a la DGII</p>
        <p className="text-xs text-gray-500">
          Irreversible: cada e-NCF queda consumido y su secuencia no se puede reutilizar en otro
          intento. Los ya enviados se saltan automáticamente.
        </p>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={handleRun}
            disabled={!file || !preview || sending || !listoParaEnviar}
            title={!preview ? 'Primero firma sin enviar' : undefined}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            <i
              className={`${sending ? 'ri-loader-4-line animate-spin' : 'ri-send-plane-line'} mr-2`}
            ></i>
            {sending ? 'Enviando a la DGII...' : 'Enviar simulación'}
          </button>

          {fallidos > 0 && (
            <button
              onClick={handleRetry}
              disabled={!file || retrying || !listoParaEnviar}
              title={!file ? 'Carga el mismo set que usaste para enviar' : undefined}
              className="px-4 py-2 rounded-lg border border-amber-300 bg-amber-50 text-amber-800 text-sm font-medium hover:bg-amber-100 transition-colors whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <i
                className={`${retrying ? 'ri-loader-4-line animate-spin' : 'ri-restart-line'} mr-2`}
              ></i>
              Reintentar {fallidos} con números nuevos
            </button>
          )}

          <button
            onClick={handleRefresh}
            disabled={refreshing || !results.some((r) => r.status === 'enproceso')}
            title="La DGII valida en diferido; esto vuelve a consultar los que quedaron en proceso"
            className="px-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 text-sm font-medium hover:bg-gray-50 transition-colors whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <i
              className={`${refreshing ? 'ri-loader-4-line animate-spin' : 'ri-refresh-line'} mr-2`}
            ></i>
            Actualizar estados
          </button>

          {results.length > 0 && (
            <button
              onClick={handleReset}
              className="px-4 py-2 rounded-lg border border-gray-300 bg-white text-red-600 text-sm font-medium hover:bg-red-50 transition-colors whitespace-nowrap"
            >
              <i className="ri-delete-bin-line mr-2"></i>
              Reiniciar registro
            </button>
          )}
        </div>
      </div>

      {/* Mismo paso manual que en el Paso 2 para los consumo < 250 mil */}
      {rfceAceptados > 0 && (
        <div className="bg-blue-50 border border-blue-200 text-blue-800 rounded-lg px-4 py-3 text-sm">
          <p className="font-medium mb-1">
            <i className="ri-information-line mr-1"></i>
            Falta un paso manual en el portal
          </p>
          <p className="text-xs">
            {rfceAceptados} resúmenes de consumo fueron aceptados. Ahora hay que subir esas facturas{' '}
            <strong>íntegras</strong> en el formulario "Facturas de consumo &lt; 250Mil" de este
            mismo paso 4 del portal. Descarga su XML con el botón de cada fila: es el archivo
            firmado exacto que generó el resumen aceptado.
          </p>
        </div>
      )}

      {/* Resultados */}
      {filas.length > 0 && (
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 flex items-center justify-between flex-wrap gap-2">
            <p className="text-sm font-medium text-gray-900">
              Comprobantes de la simulación
              <span className="ml-2 text-xs font-normal text-gray-500">
                ({filas.length} en total)
              </span>
            </p>
            {results.length > 0 && (
              <p className="text-xs text-gray-600">
                Aceptados: <strong>{ecfAceptados}</strong> íntegros ·{' '}
                <strong>{rfceAceptados}</strong> resúmenes
              </p>
            )}
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    e-NCF
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tipo
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Vía
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Estado
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Código seg.
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    XML
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filas.map((r) => {
                  const badge = STATUS_BADGE[r.status] ?? STATUS_BADGE.pendiente;
                  return (
                    <tr key={r.encf} className="hover:bg-gray-50 align-top">
                      <td className="px-4 py-2 whitespace-nowrap text-xs font-mono font-semibold text-gray-900">
                        {r.encf}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                        {r.doc_type}
                        <span className="block text-xs text-gray-500">
                          {DOC_TYPE_LABEL[r.doc_type]}
                        </span>
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-xs text-gray-600">
                        {r.send_via === 'rfce' ? 'Resumen' : 'Íntegro'}
                      </td>
                      <td className="px-4 py-2">
                        <EcfBadge variant={badge.variant}>{badge.label}</EcfBadge>
                        {r.message && (
                          <span className="block text-xs text-gray-500 mt-1 max-w-md">
                            {r.message}
                          </span>
                        )}
                        {r.track_id && (
                          <span className="block text-[11px] font-mono text-gray-400 mt-1">
                            {r.track_id}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-xs font-mono text-gray-700">
                        {r.security_code ?? '—'}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap">
                        {r.has_xml && results.length > 0 ? (
                          <button
                            onClick={() => descargar(r.encf)}
                            className="text-blue-600 hover:text-blue-900"
                            title="Descargar XML firmado"
                          >
                            <i className="ri-download-line"></i>
                          </button>
                        ) : (
                          <span className="text-xs text-gray-400">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
